import { Badge } from '@/components/ui/badge'
import { AlertCircle, CheckCircle2, XCircle, BookOpen, ChevronRight, Goal } from 'lucide-react'
import type { PendingObjective } from './types'
import { getFunctionalAreaColor } from './types'
import { objectiveYear } from '@/lib/objectiveYear'

interface ObjectiveMasterListProps {
  objectives: PendingObjective[]
  selectedId: number | null
  onSelect: (id: number) => void
  emptyLabel: string
}

function statusMeta(status: string) {
  if (status === 'pending_approval') return { Icon: AlertCircle, color: 'text-amber-600', dot: 'border-l-amber-500', label: 'Pending', badge: 'bg-amber-100 text-amber-800' }
  if (status === 'active') return { Icon: CheckCircle2, color: 'text-emerald-600', dot: 'border-l-emerald-500', label: 'Active', badge: 'bg-emerald-100 text-emerald-800' }
  return { Icon: XCircle, color: 'text-red-600', dot: 'border-l-red-500', label: 'Rejected', badge: 'bg-red-100 text-red-800' }
}

/**
 * Compact, selectable list of objectives — the "master" pane of the
 * master-detail layout. Selecting a row drives the detail/editor pane.
 */
export default function ObjectiveMasterList({ objectives, selectedId, onSelect, emptyLabel }: ObjectiveMasterListProps) {
  if (objectives.length === 0) {
    return (
      <div className="py-12 px-4 text-center text-muted-foreground">
        <Goal className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
        <p className="text-sm">{emptyLabel}</p>
      </div>
    )
  }

  return (
    <div className="divide-y">
      {objectives.map((objective) => {
        const meta = statusMeta(objective.status)
        const Icon = meta.Icon
        const selected = selectedId === objective.id
        const year = objectiveYear(objective)
        return (
          <button
            key={objective.id}
            type="button"
            onClick={() => onSelect(objective.id)}
            aria-current={selected}
            className={`w-full text-left px-3 py-3 border-l-4 ${meta.dot} flex items-start gap-2 transition-colors ${
              selected ? 'bg-heritage-blue/5 ring-1 ring-inset ring-heritage-blue/30' : 'hover:bg-gray-50'
            }`}
          >
            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.color}`} />
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium leading-snug line-clamp-2 ${selected ? 'text-heritage-blue' : 'text-gray-900'}`}>
                {objective.title}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${meta.badge}`}>{meta.label}</Badge>
                {year && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0" title={`Objective for ${year}`}>
                    {year}
                  </Badge>
                )}
                {objective.kpa && (
                  <Badge className={`text-[10px] px-1.5 py-0 ${getFunctionalAreaColor(objective.kpa.name)}`}>
                    <BookOpen className="h-2.5 w-2.5 mr-1" />
                    {objective.kpa.display_name}
                  </Badge>
                )}
                {objective.status === 'active' && (
                  <span className="text-[10px] text-muted-foreground">{objective.progress || 0}%</span>
                )}
              </div>
            </div>
            <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 ${selected ? 'text-heritage-blue' : 'text-gray-300'}`} />
          </button>
        )
      })}
    </div>
  )
}
